import { z } from 'zod/v4';

/**
 * Body of `POST /risk-analysis/blueprint-companies/bulk`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/blueprint-company.js` →
 * `bulkCreate.body`.
 *
 * Unlike the assessment-calculate bodies, `companyIds` has **no `minItems`** here, so
 * an empty array is valid and resolves to a 201 that links nothing. Preserved
 * deliberately — adding `.min(1)` would turn existing 201s into 400s.
 */
export const BulkCreateBlueprintCompaniesBodySchema = z.object({
  companyIds: z.array(z.string().min(1).max(36)),
  assessmentBlueprintId: z.string().min(1).max(36),
});

export type BulkCreateBlueprintCompaniesBody = z.infer<
  typeof BulkCreateBlueprintCompaniesBodySchema
>;

/**
 * `GET /risk-analysis/blueprint-companies/:id/prioritization-details` params,
 * ported from `2025-06/risk-analysis-service/routes/schemas/v3/request/blueprint-company.js`'s
 * `listPrioritizationDetails.params`.
 */
export const GetBlueprintPrioritizationDetailsParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetBlueprintPrioritizationDetailsParams = z.infer<
  typeof GetBlueprintPrioritizationDetailsParamsSchema
>;

export const LIST_BLUEPRINT_PRIORITIZATION_DETAILS_DEFAULT_PAGE_SIZE = 15;
export const LIST_BLUEPRINT_PRIORITIZATION_DETAILS_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/blueprint-companies/:id/prioritization-details`
 * querystring, ported from `listPrioritizationDetails.querystring`
 * (`...paginationParams, filter, sort`). Neither `filter` nor `sort` carries a
 * `pattern`/enum restriction in the legacy AJV schema (unlike the sibling
 * `assessment-blueprints` list), so any string is accepted here too — the
 * column-mapping lookup in `listBlueprintPrioritizationDetailsDb` is what
 * actually validates the value: an unmapped `sort` column is silently dropped
 * (falls back to the default order), while an unmapped `filter` key throws a
 * `missed_column_mapping_setting` 400.
 */
export const GetBlueprintPrioritizationDetailsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_BLUEPRINT_PRIORITIZATION_DETAILS_MAX_PAGE_SIZE)
    .default(LIST_BLUEPRINT_PRIORITIZATION_DETAILS_DEFAULT_PAGE_SIZE),
  filter: z.string().optional(),
  sort: z.string().optional(),
});

export type GetBlueprintPrioritizationDetailsQuery = z.infer<
  typeof GetBlueprintPrioritizationDetailsQuerySchema
>;

/**
 * One row of `GET /risk-analysis/blueprint-companies/:id/prioritization-details`,
 * ported from the legacy `blueprintCompanyPrioritization` response definition
 * (`2025-06/risk-analysis-service/routes/schemas/v3/responses.js`). That
 * schema's `additionalProperties: false` strips the SQL's `company_name`
 * alias (selected only so `ORDER BY` can reference it) — it never reaches the
 * client, matching `frontend-v2`'s `BlueprintPrioritizationApiItem` comment.
 *
 * The three scores are nullable here even though the legacy JSON schema
 * declared them as plain non-nullable `number` — each is a conditional
 * `AVG(...)` over the assessment's metric results and is `NULL` in Postgres
 * when every candidate value was `<= 0`, so the real wire shape can and does
 * emit `null` regardless of what the legacy schema claimed.
 */
export const BlueprintCompanyPrioritizationRowSchema = z.object({
  assessedCompanyId: z.string().min(1).max(36),
  companyData: z.record(z.string(), z.unknown()),
  // Output of the blueprint's own score formula (e.g. likelihood × severity),
  // so unbounded above — staging holds values up to 50. The legacy
  // `maximum: 5` was never enforced: fast-json-stringify ignores it.
  // Likelihood/severity are clamped to 1–5 at calculation time.
  priorityScore: z.number().min(0).nullable(),
  likelihoodScore: z.number().min(0).max(5).nullable(),
  severityScore: z.number().min(0).max(5).nullable(),
});

export type BlueprintCompanyPrioritizationRow = z.infer<
  typeof BlueprintCompanyPrioritizationRowSchema
>;

/**
 * `GET /risk-analysis/blueprint-companies/:id` params, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/blueprint-company.js`'s
 * `list.params`.
 */
export const GetBlueprintCompaniesParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetBlueprintCompaniesParams = z.infer<
  typeof GetBlueprintCompaniesParamsSchema
>;

export const LIST_BLUEPRINT_COMPANIES_DEFAULT_PAGE_SIZE = 15;
export const LIST_BLUEPRINT_COMPANIES_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/blueprint-companies/:id` querystring, ported from
 * `list.querystring` (`...paginationParams, filter, sort`). Neither `filter`
 * nor `sort` carries a `pattern`/enum restriction in the legacy AJV schema, so
 * any string is accepted here too — the column-mapping lookup in
 * `listBlueprintCompaniesDb` is what actually validates the value: an unmapped
 * `filter` key throws a `missed_column_mapping_setting` 400, while an unmapped
 * `sort` column is silently dropped (falls back to the default order).
 */
export const GetBlueprintCompaniesQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_BLUEPRINT_COMPANIES_MAX_PAGE_SIZE)
    .default(LIST_BLUEPRINT_COMPANIES_DEFAULT_PAGE_SIZE),
  filter: z.string().optional(),
  sort: z.string().optional(),
});

export type GetBlueprintCompaniesQuery = z.infer<
  typeof GetBlueprintCompaniesQuerySchema
>;

/**
 * One row of `GET /risk-analysis/blueprint-companies/:id`, ported from the
 * legacy `blueprintCompany` response definition
 * (`2025-06/risk-analysis-service/routes/schemas/v3/responses.js`). That
 * schema is `additionalProperties: false` with no `required` array — every
 * field can be absent/null depending on the join/aggregate that produces it —
 * matched here by making every field beyond the two ids nullable.
 */
/**
 * `value`/`unit`/`year` requires all three together, but production rows
 * exist with only `{ value: null }` — e.g. an answer cleared without
 * resubmitting the full object (same issue fixed for the sibling
 * `risk-landscape` response in `RiskLandscapeCompanyOrderVolumeSchema`).
 * Each key is nullable rather than optional so the transformer can backfill
 * missing ones with `null`.
 */
export const BlueprintCompanyAnnualPurchaseVolumeSchema = z
  .object({
    value: z.string().min(1).nullable(),
    unit: z.string().min(1).nullable(),
    year: z.string().min(1).nullable(),
  })
  .nullable();

export const BlueprintCompanyRowSchema = z.object({
  id: z.string().min(1).max(36),
  assessedCompanyId: z.string().min(1).max(36),
  netScore: z.number().min(0).max(5).nullable(),
  status: z.string(),
  companyData: z.record(z.string(), z.unknown()),
  grossValue: z.number().min(0).max(5).nullable(),
  companySize: z.string().min(1).nullable(),
  annualPurchaseVolume: BlueprintCompanyAnnualPurchaseVolumeSchema,
  productTypes: z.array(z.object({ id: z.string().min(1) })).nullable(),
  collaborationStatus: z.string().min(1).nullable(),
  averagePriorityScore: z.number().min(1).nullable(),
  minAveragePriorityScore: z.number().nullable(),
  maxAveragePriorityScore: z.number().nullable(),
});

export type BlueprintCompanyRow = z.infer<typeof BlueprintCompanyRowSchema>;
